<template>
  <Dialog
    :model-value="visible"
    :title="`Add New ${isProgram ? 'Program' : 'Workflow'} Assignees`"
    dialog-type="primary"
    dialog-size="small"
    :close-on-overlay="false"
    :header-icon-src="require('@/assets/MyPatient/AddPatient_white.svg')"
    @update:model-value="(v) => { if (!v) closeDialog() }"
    @close="closeDialog"
  >
    <div class="dialog-content">
      <div class="content-section">
        <!-- Enrollment Warning -->
        <div v-if="hasEnrollmentLimit" class="enrollment-info" :class="{ 'warning': selectedPatients.length >= availableSlots || isMaxEnrollmentReached }">
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path d="M10 0C4.48 0 0 4.48 0 10C0 15.52 4.48 20 10 20C15.52 20 20 15.52 20 10C20 4.48 15.52 0 10 0ZM11 15H9V13H11V15ZM11 11H9V5H11V11Z" :fill="isMaxEnrollmentReached || selectedPatients.length >= availableSlots ? '#D10000' : '#004879'"/>
          </svg>
          <span class="enrollment-text">{{ enrollmentWarningMessage }}</span>
        </div>

        <!-- Select Patients Field -->
        <div class="field-group">
          <label class="field-label">Select Patients</label>

          <!-- MultiSelector Component -->
          <MultiSelector
            v-model="selectedPatientValues"
            :options="patientOptions"
            :remote_search="true"
            :remote_search_pagination="true"
            :remote_search_has_more="hasMoreResults"
            :remote_search_load_more_loading="isLoading"
            :loading="isLoading && allPatients.length === 0"
            placeholder="Select patients"
            size="medium"
            dropdown_max_height="300px"
            @search="handlePatientSearch"
            @remote-search-load-more="handleLoadMorePatients"
          >
            <template #option="{ option, is_selected }">
              <div class="patient-option-custom">
                <span class="patient-option-name">{{ option.label }}</span>
                <span class="patient-option-meta">ID: #{{ option.value }}</span>
              </div>
            </template>
          </MultiSelector>
        </div>

        <!-- Auto Apply Checkbox - Only show for workflows, not programs -->
        <div v-if="!isProgram" class="auto-apply-wrapper">
          <div class="checkbox-wrapper">
            <div class="custom-checkbox" :class="{ checked: autoApplyToNew }" @click="autoApplyToNew = !autoApplyToNew">
              <svg v-if="autoApplyToNew" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M6.00016 10.7801L3.22016 8.00009L2.2735 8.94676L6.00016 12.6734L14.0002 4.67342L13.0535 3.72676L6.00016 10.7801Z" fill="white"/>
              </svg>
            </div>
          </div>
          <span class="checkbox-label">Automatically apply to new patients</span>
        </div>
      </div>
    </div>

    <template #footer>
      <ResponsiveButton button_type="outlined" built_in_theme="error" size="medium" @click="closeDialog">Cancel</ResponsiveButton>
      <ResponsiveButton button_type="filled" built_in_theme="dark" size="medium" @click="saveSelection">Save</ResponsiveButton>
    </template>
  </Dialog>
</template>

<script>
import { getPatientListApi } from '@/api/patientList';
import { batchAssignPatientsToWorkflowApi } from '@/api/workflowManage';
import { notify } from '@/utils/notify';

export default {
  name: 'AddNewAssigneeDialog',
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    existingPatientIds: {
      type: Array,
      default: () => []
    },
    applyToNewPatients: {
      type: Boolean,
      default: false
    },
    isProgram: {
      type: Boolean,
      default: false
    },
    workflowId: {
      type: [String, Number],
      default: null
    },
    maximumEnrollment: {
      type: Number,
      default: null
    },
    currentEnrollment: {
      type: Number,
      default: 0
    }
  },
  emits: ['close', 'success'],
  data() {
    return {
      autoApplyToNew: false,
      selectedPatients: [],
      allPatients: [],
      currentPage: 1,
      perPage: 20,
      totalResults: 0,
      isLoading: false,
      hasMoreResults: true,
      searchDebounceTimer: null,
      currentSearchQuery: ''
    };
  },
  computed: {
    dialogVisible: {
      get() {
        return this.visible;
      },
      set(value) {
        if (!value) {
          this.$emit('close');
        }
      }
    },
    filteredPatients() {
      return this.allPatients.filter(patient => {
        return !this.existingPatientIds.includes(patient.id) &&
               !this.existingPatientIds.includes(String(patient.id));
      });
    },
    patientOptions() {
      // Transform filtered patients to MultiSelector options
      return this.filteredPatients.map(patient => ({
        value: String(patient.id),
        label: patient.name,
        disabled: false,
        originalData: patient
      }));
    },
    selectedPatientValues: {
      get() {
        // Return selected patient IDs as strings
        return this.selectedPatients.map(p => String(p.id));
      },
      set(values) {
        // Convert selected IDs back to patient objects
        this.selectedPatients = values
          .map(id => this.allPatients.find(p => String(p.id) === id))
          .filter(p => p); // Remove undefined values
      }
    },
    hasEnrollmentLimit() {
      return this.maximumEnrollment !== null && this.maximumEnrollment > 0;
    },
    availableSlots() {
      if (!this.hasEnrollmentLimit) return Infinity;
      return Math.max(0, this.maximumEnrollment - this.currentEnrollment);
    },
    isMaxEnrollmentReached() {
      return this.hasEnrollmentLimit && this.currentEnrollment >= this.maximumEnrollment;
    },
    canAddMorePatients() {
      if (!this.hasEnrollmentLimit) return true;
      return this.selectedPatients.length < this.availableSlots;
    },
    enrollmentWarningMessage() {
      if (!this.hasEnrollmentLimit) return '';
      if (this.isMaxEnrollmentReached) {
        return `Maximum enrollment of ${this.maximumEnrollment} has been reached. Cannot add more patients.`;
      }
      if (this.selectedPatients.length >= this.availableSlots) {
        return `You can only add ${this.availableSlots} more patient(s). Maximum enrollment: ${this.maximumEnrollment}, Current: ${this.currentEnrollment}`;
      }
      return `${this.availableSlots} slot(s) available (Max: ${this.maximumEnrollment}, Current: ${this.currentEnrollment})`;
    }
  },
  watch: {
    visible(newVal) {
      if (newVal) {
        // Initialize autoApplyToNew from prop when dialog opens
        this.autoApplyToNew = this.applyToNewPatients;
        // Load initial patients to populate the dropdown
        this.handlePatientSearch('');
      } else {
        // Clear debounce timer when dialog closes
        if (this.searchDebounceTimer) {
          clearTimeout(this.searchDebounceTimer);
          this.searchDebounceTimer = null;
        }
      }
    }
  },
  methods: {
    async loadPatients(reset = false) {
      if (this.isLoading) return;

      // Check if we've already loaded all results
      if (!reset && !this.hasMoreResults) return;

      try {
        this.isLoading = true;

        // Reset pagination if needed
        if (reset) {
          this.currentPage = 1;
          this.allPatients = [];
          this.hasMoreResults = true;
        }

        const response = await getPatientListApi({
          search_patient_field: this.currentSearchQuery,
          search_patient_type: 'patient_name',
          page: String(this.currentPage),
          perPage: String(this.perPage)
        });

        console.log('Full API response:', response);

        // API response structure: { total_count: number, patients: array }
        if (response && response.patients && Array.isArray(response.patients)) {
          const patients = response.patients.map(patient => ({
            id: patient.patient_id,
            name: `${patient.patient_first_name} ${patient.patient_last_name}`.trim()
          }));

          console.log('Mapped patients:', patients);

          if (reset) {
            this.allPatients = patients;
          } else {
            this.allPatients = [...this.allPatients, ...patients];
          }

          // Update total and check if there are more results
          this.totalResults = response.total_count || 0;
          const loadedCount = this.allPatients.length;
          this.hasMoreResults = loadedCount < this.totalResults;

          console.log(`Loaded ${patients.length} patients. Total: ${loadedCount}/${this.totalResults}`);
        } else {
          console.warn('Invalid response structure:', response);
        }
      } catch (error) {
        console.error('Error loading patients:', error);
      } finally {
        this.isLoading = false;
      }
    },

    /**
     * Handle search from MultiSelector
     */
    handlePatientSearch(query) {
      console.log('Search event triggered with query:', query);
      if (this.searchDebounceTimer) {
        clearTimeout(this.searchDebounceTimer);
      }

      this.searchDebounceTimer = setTimeout(async () => {
        this.currentSearchQuery = query;
        await this.loadPatients(true);
      }, 500);
    },

    /**
     * Handle load more from MultiSelector pagination
     */
    async handleLoadMorePatients(query) {
      console.log('Load more event triggered with query:', query);
      console.log('Current state - isLoading:', this.isLoading, 'hasMoreResults:', this.hasMoreResults, 'currentPage:', this.currentPage);
      if (this.isLoading || !this.hasMoreResults) {
        console.log('Load more blocked');
        return;
      }

      this.currentPage++;
      await this.loadPatients(false);
    },

    closeDialog() {
      this.resetForm();
      this.$emit('close');
    },
    async saveSelection() {
      if (!this.workflowId || this.selectedPatients.length === 0) {
        notify({
          title: 'Error',
          message: 'Please select at least one patient',
          type: 'error',
          showClose: true
        });
        return;
      }

      // Check if adding these patients would exceed maximum enrollment
      if (this.hasEnrollmentLimit) {
        const newTotal = this.currentEnrollment + this.selectedPatients.length;
        if (newTotal > this.maximumEnrollment) {
          notify({
            title: 'Enrollment Limit Exceeded',
            message: `Cannot add ${this.selectedPatients.length} patient(s). Only ${this.availableSlots} slot(s) available.`,
            type: 'error',
            showClose: true
          });
          return;
        }
      }

      try {
        const patientIds = this.selectedPatients.map(p => p.id);
        
        await batchAssignPatientsToWorkflowApi(this.workflowId, {
          patient_ids: patientIds
        });

        notify({
          title: 'Success',
          message: `Successfully added ${patientIds.length} patient(s)`,
          type: 'success',
          showClose: true
        });

        // Emit success event with patient data
        this.$emit('success', {
          patients: this.selectedPatients,
          autoApplyToNew: this.autoApplyToNew
        });

        this.resetForm();
        this.$emit('close');
      } catch (error) {
        console.error('Error adding patients:', error);
        notify({
          title: 'Error',
          message: error.response?.data?.message || 'Failed to add patients',
          type: 'error',
          showClose: true
        });
      }
    },
    resetForm() {
      this.selectedPatients = [];
      this.autoApplyToNew = false;
      this.allPatients = [];
      this.currentPage = 1;
      this.totalResults = 0;
      this.hasMoreResults = true;
      this.currentSearchQuery = '';

      // Clear debounce timer
      if (this.searchDebounceTimer) {
        clearTimeout(this.searchDebounceTimer);
        this.searchDebounceTimer = null;
      }
    }
  }
};
</script>

<style scoped>
/* Enrollment Info */
.enrollment-info {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  background: #E3F2FD;
  border: 1px solid #004879;
  border-radius: 8px;
  margin-bottom: 16px;
}

.enrollment-info.warning {
  background: #FFEBEE;
  border-color: #D10000;
}

.enrollment-text {
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  color: #004879;
}

.enrollment-info.warning .enrollment-text {
  color: #D10000;
}

/* Header */
.dialog-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 24px;
  background: #004879;
  border-radius: 10px 10px 0 0;
}

.header-content {
  display: flex;
  align-items: center;
  gap: 4px;
}

.header-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
}

.header-title {
  font-family: 'Inter', sans-serif;
  font-size: 20px;
  font-weight: 600;
  line-height: normal;
  color: #FFFFFF;
}

.close-button {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 0;
}

.close-button:hover {
  opacity: 0.8;
}

/* Content */
.dialog-content {
  padding: 16px 8px;
  background: #F9FBFD;
}

.content-section {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 12px 0px;
}

.field-group {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.field-label {
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  letter-spacing: 0.1px;
  color: #4A4A4A;
}

/* Custom Patient Option Styles for MultiSelector */
.patient-option-custom {
  display: flex;
  flex-direction: column;
  gap: 2px;
  width: 100%;
  padding: 4px 0;
}

.patient-option-name {
  font-family: 'IBM Plex Sans Hebrew', sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  color: #333333;
}

.patient-option-meta {
  font-family: 'Inter', sans-serif;
  font-size: 12px;
  font-weight: 400;
  line-height: 16px;
  color: #666666;
}

/* Auto Apply */
.auto-apply-wrapper {
  display: flex;
  align-items: center;
  gap: 6px;
  align-self: flex-end;
  margin-top: 4px;
}

.checkbox-label {
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  letter-spacing: 0.25px;
  color: #333333;
  cursor: pointer;
  user-select: none;
}

/* Footer */
.dialog-footer {
  display: flex;
  flex-direction: column;
  padding: 0 24px 24px;
  background: #F9FBFD;
  border-radius: 0 0 10px 10px;
}

.footer-divider {
  height: 1px;
  background: #C4C4C4;
  border-style: dashed;
  margin-bottom: 24px;
}

.footer-actions {
  display: flex;
  align-items: center;
  justify-content: space-between;
}
</style>
